import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Drives the Add/Edit Custom Question modal (see _form_modal.html.erb). It:
//  • upgrades the type picker to select2 (the modal is turbo-injected, so the
//    global turbo:load init never fires on it — we init here and bind change),
//  • reveals the Choices repeater (dropdown/radio/checkboxes) OR the File Upload
//    config inset based on the selected type (the type-config contract, §05),
//  • forces Required ON + disables the toggle for Terms & Conditions (a consent
//    can't be optional, §08),
//  • add/remove/reorder choices (min-one enforced by disabling the last delete),
//  • toggles the "More options" disclosure (placeholder / help text).
const CHOICE_TYPES = ["dropdown", "radio_buttons", "checkboxes"]
const TERMS_TYPE = "terms_checkbox"
const FILE_TYPE = "file_upload"

export default class extends Controller {
  static targets = [
    "typeSelect", "extSelect", "optionsContainer", "fileOptions", "choicesList", "choiceItem",
    "choiceInput", "removeChoice", "requiredToggle", "requiredRow", "requiredHint",
    "moreBody", "moreToggle", "moreToggleLabel"
  ]

  connect() {
    this.initTypeSelect()
    this.initExtSelect()
    this.handleTypeChange()
    this.initChoiceSorting()
  }

  disconnect() {
    if (this.sortable) this.sortable.destroy()
    this.destroySelect2(this.hasTypeSelectTarget ? this.typeSelectTarget : null)
    this.destroySelect2(this.hasExtSelectTarget ? this.extSelectTarget : null)
  }

  destroySelect2(el) {
    if (el && window.jQuery) {
      const $el = window.jQuery(el)
      if ($el.data("select2")) $el.select2("destroy")
    }
  }

  // The allowed-file-types multi-select. The modal is turbo-injected so the
  // global turbo:load init never runs on it — init here like the type picker.
  initExtSelect() {
    if (!this.hasExtSelectTarget) return
    const el = this.extSelectTarget
    // Parent the dropdown to document.body (NOT the .select2-search-off wrapper,
    // which sits inside the scrollable .modal-body). When parented inside the
    // scrollable body, scrolling the open option list scrolls the body instead,
    // which select2 reads as a blur and auto-closes the dropdown — so the user
    // can't reach the lower file types. Body-parented + tagged with
    // .pq-ext-dropdown so the scoped SCSS styles only this panel (same approach
    // as the type picker).
    const extOpts = {
      minimumResultsForSearch: Infinity,
      dropdownParent: document.body,
      dropdownCssClass: "pq-ext-dropdown",
    }
    if (typeof window.initSelect2 === "function") {
      window.initSelect2(el, null, extOpts)
    } else if (window.jQuery && window.jQuery.fn.select2) {
      window.jQuery(el).select2(extOpts)
    }
  }

  // ── Type picker (select2) ────────────────────────────────────────────────
  initTypeSelect() {
    if (!this.hasTypeSelectTarget) return
    const el = this.typeSelectTarget
    // Prefer the app's themed initializer; fall back to a bare select2 call.
    // Question Type is a single REQUIRED select — there is always a type, so it
    // must NOT be clearable. The global initSelect2 defaults allowClear:true for
    // every .select2; override it to false here for THIS instance only (the
    // options object is spread after the defaults). This keeps the stray "✕"
    // clear off the trigger without touching custom.js or the ext multi-select.
    // Parent the dropdown to document.body so the open panel isn't clipped.
    // History: it was originally clipped to ~4 rows by the scrollable .modal-body,
    // then moved INTO .pq-modal — but that shell has overflow:hidden + a
    // max-height, so the panel got clipped at the modal's BOTTOM edge instead
    // (lower types hidden behind the modal). Body-parented, select2 positions the
    // panel absolutely, anchored to the trigger, so it can extend beyond the modal
    // without being clipped by either container. dropdownCssClass tags the panel
    // (.pq-type-dropdown) so the scoped SCSS reaches it wherever it mounts — and
    // only this picker's panel, never the Reports/Financials/ext dropdowns.
    const dropdownParent = document.body
    const typeOpts = {
      minimumResultsForSearch: Infinity,
      allowClear: false,
      dropdownParent,
      dropdownCssClass: "pq-type-dropdown",
      // Render each option (and the selected trigger) with its lucide icon.
      // The icon SVG is pre-rendered server-side into the <option>'s
      // data-icon-svg (via the lucide_icon helper), so we just inject it.
      templateResult: this.typeOptionTemplate.bind(this),
      templateSelection: this.typeOptionTemplate.bind(this),
      escapeMarkup: (m) => m,
    }
    if (typeof window.initSelect2 === "function") {
      window.initSelect2(el, dropdownParent || null, typeOpts)
    } else if (window.jQuery && window.jQuery.fn.select2) {
      window.jQuery(el).select2(typeOpts)
    }
    // select2 dispatches a jQuery `change`; bind through jQuery so it reaches us
    // regardless of native-event propagation. Also bind native change as a
    // belt-and-suspenders (covers the no-select2 fallback).
    el.addEventListener("change", () => this.handleTypeChange())
    if (window.jQuery) {
      window.jQuery(el).on("select2:select", () => this.handleTypeChange())
    }
  }

  // select2 templateResult/templateSelection formatter: prepend the option's
  // pre-rendered lucide SVG (from <option data-icon-svg>) before its label.
  // escapeMarkup is set to passthrough, so we return a DOM node (jQuery) to keep
  // select2 from re-escaping — and to avoid injecting raw strings.
  typeOptionTemplate(state) {
    if (!state || !state.id) return state ? state.text : ""
    const svg = state.element ? state.element.getAttribute("data-icon-svg") : null
    const label = this.escapeHtml(state.text)
    const html = svg
      ? `<span class="pq-type-opt"><span class="pq-type-opt-ic">${svg}</span><span>${label}</span></span>`
      : `<span class="pq-type-opt">${label}</span>`
    return window.jQuery ? window.jQuery(html) : html
  }

  escapeHtml(s) {
    const d = document.createElement("div")
    d.textContent = s == null ? "" : String(s)
    return d.innerHTML
  }

  selectedType() {
    return this.hasTypeSelectTarget ? this.typeSelectTarget.value : "short_text"
  }

  // ── Type-driven reveal + Terms required-lock ─────────────────────────────
  handleTypeChange() {
    const type = this.selectedType()
    const requiresOptions = CHOICE_TYPES.includes(type)
    const isFile = type === FILE_TYPE
    const isTerms = type === TERMS_TYPE

    if (this.hasOptionsContainerTarget) {
      this.optionsContainerTarget.style.display = requiresOptions ? "" : "none"
      this.choiceInputTargets.forEach((input) => {
        if (requiresOptions) {
          input.setAttribute("required", "required")
        } else {
          input.removeAttribute("required")
        }
      })
    }

    if (this.hasFileOptionsTarget) {
      this.fileOptionsTarget.style.display = isFile ? "" : "none"
    }

    this.applyTermsLock(isTerms)
  }

  // Terms & Conditions: force Required ON and disable the toggle so the server
  // still records required:true (a disabled checkbox submits, but we keep it
  // checked; an explicit hidden mirror guarantees the value posts).
  applyTermsLock(isTerms) {
    if (!this.hasRequiredToggleTarget) return
    const toggle = this.requiredToggleTarget

    const wrap = toggle.closest(".sg-toggle")
    if (isTerms) {
      this._prevRequired = toggle.checked
      toggle.checked = true
      toggle.disabled = true
      // Setting .checked via JS doesn't always repaint the :checked pseudo on a
      // disabled input; mirror the on-state with a class the SCSS also honors.
      if (wrap) wrap.classList.add("is-on")
      this.ensureRequiredMirror(true)
      if (this.hasRequiredHintTarget) {
        this.requiredHintTarget.textContent = this.requiredLockedHint()
      }
      if (this.hasRequiredRowTarget) this.requiredRowTarget.classList.add("is-locked")
    } else {
      toggle.disabled = false
      if (wrap) wrap.classList.toggle("is-on", toggle.checked)
      this.ensureRequiredMirror(false)
      if (this._prevRequired !== undefined) {
        toggle.checked = this._prevRequired
        this._prevRequired = undefined
      }
      if (this.hasRequiredHintTarget) {
        this.requiredHintTarget.textContent = this.requiredHint()
      }
      if (this.hasRequiredRowTarget) this.requiredRowTarget.classList.remove("is-locked")
    }
  }

  // A disabled checkbox is omitted from the form submission. When we lock the
  // Terms toggle ON we add a hidden field so required=true still posts.
  ensureRequiredMirror(on) {
    const existing = this.element.querySelector('input[type="hidden"][data-required-mirror]')
    if (on) {
      if (!existing) {
        const hidden = document.createElement("input")
        hidden.type = "hidden"
        hidden.name = this.requiredToggleTarget.name // personalized_question[required]
        hidden.value = "1"
        hidden.setAttribute("data-required-mirror", "true")
        this.requiredToggleTarget.insertAdjacentElement("afterend", hidden)
      }
    } else if (existing) {
      existing.remove()
    }
  }

  requiredHint() {
    return this._i18n("required_hint")
  }
  requiredLockedHint() {
    return this._i18n("required_locked_hint")
  }

  // ── Choices repeater ─────────────────────────────────────────────────────
  addChoice(event) {
    event.preventDefault()
    const newChoice = this.buildChoiceRow()
    this.choicesListTarget.appendChild(newChoice)
    this.refreshChoiceState()
    const input = newChoice.querySelector("input[type='text']")
    if (input) input.focus()
  }

  removeChoice(event) {
    event.preventDefault()
    const row = event.currentTarget.closest('[data-question-form-target="choiceItem"]')
    const count = this.choiceItemTargets.length
    if (count > 1 && row) row.remove()
    this.refreshChoiceState()
  }

  // Clone the first row so the grip + delete markup is preserved exactly, then
  // blank the input. Re-number aria-labels after any add/remove.
  buildChoiceRow() {
    const template = this.choiceItemTargets[0]
    const clone = template.cloneNode(true)
    const input = clone.querySelector("input[type='text']")
    if (input) {
      input.value = ""
      const type = this.selectedType()
      if (CHOICE_TYPES.includes(type)) input.setAttribute("required", "required")
    }
    return clone
  }

  // Disable the delete on the last remaining row (min-one), enable otherwise,
  // and keep aria-labels / titles in sync.
  refreshChoiceState() {
    const items = this.choiceItemTargets
    const onlyOne = items.length <= 1
    items.forEach((item, i) => {
      const del = item.querySelector('[data-question-form-target="removeChoice"]')
      const input = item.querySelector("input[type='text']")
      if (del) {
        del.disabled = onlyOne
        const label = onlyOne ? this._i18n("remove_choice_min") : this._i18n("remove_choice")
        del.setAttribute("title", label)
        del.setAttribute("aria-label", label)
      }
      if (input) {
        input.setAttribute("aria-label", this._i18n("choice_label", i + 1))
      }
    })
  }

  // HTML5 drag-and-drop reorder via SortableJS (already vendored for the
  // question list). The choices array order = display order, persisted on
  // submit because the inputs keep their DOM order.
  initChoiceSorting() {
    if (!this.hasChoicesListTarget) return
    this.sortable = Sortable.create(this.choicesListTarget, {
      animation: 150,
      handle: ".st-choice-grip",
      draggable: '[data-question-form-target="choiceItem"]',
      ghostClass: "st-choice-ghost",
      onEnd: () => this.refreshChoiceState()
    })
  }

  // ── More options disclosure ──────────────────────────────────────────────
  toggleMoreOptions(event) {
    event.preventDefault()
    if (!this.hasMoreBodyTarget) return
    const open = this.moreBodyTarget.style.display !== "none"
    this.moreBodyTarget.style.display = open ? "none" : ""
    if (this.hasMoreToggleTarget) {
      this.moreToggleTarget.setAttribute("aria-expanded", String(!open))
    }
    if (this.hasMoreToggleLabelTarget) {
      this.moreToggleLabelTarget.textContent = open
        ? this._i18n("more_options")
        : this._i18n("fewer_options")
    }
  }

  // ── tiny i18n shim ───────────────────────────────────────────────────────
  // Strings are passed from the server-rendered DOM where possible; this map
  // covers the few JS-generated labels. Kept in sync with en.yml
  // personalized_question.* keys.
  _i18n(key, n) {
    const dict = {
      remove_choice: "Remove choice",
      remove_choice_min: "At least one choice is required",
      more_options: "More options",
      fewer_options: "Fewer options",
      required_hint: "Attendees must answer before checkout.",
      required_locked_hint: "Consent questions are always required.",
      choice_label: `Choice ${n}`
    }
    return dict[key] || key
  }
}
